import {createSelector} from '@reduxjs/toolkit';
import {DateTime, Interval} from 'luxon';

import {OFFERS_GROUPING} from 'components/Offers/OfferGroup';
import {Offer, OfferSource, OfferType} from 'models/Offer';
import {paginateOffers} from 'store/offers/util/paginateOffers';
import {propertiesDataSelector} from 'store/properties/properties.selectors';
import {createDestinations} from 'utils/createDestinations';

import {AppState} from '../';
import {sortedMarketsSelector} from '../markets/markets.selectors';
import {OFFER_TAB, OffersState} from './offers.reducer';
import {doesOfferOverlapWithMonth} from './util/doesOfferOverlapWithMonth';

const offersState = (state: AppState): OffersState => {
  if (!state.offers) {
    console.error('Incorrect state for offers');
  }
  return state.offers;
};

export const offersSelector = createSelector(
  offersState,
  state => state.offers ?? {}
);

export const rawOffersCountSelector = createSelector(
  offersState,
  state => Object.keys(state.offers).length
);

export const isFavoriteTabSelector = createSelector(
  offersState,
  state => state.currentOfferTab === OFFER_TAB.FAVORITES
);

export const selectedOfferTabSelector = createSelector(
  offersState,
  state => state.currentOfferTab
);

export const selectedFilterSelector = createSelector(
  [offersState, isFavoriteTabSelector],
  (state, isFavoriteTab) =>
    (isFavoriteTab ? state.selectedFavoritesFilters : state.selectedFilters) ??
    {}
);

export const shouldApplyFiltersSelector = createSelector(
  selectedFilterSelector,
  selectedFilters =>
    Boolean(
      selectedFilters &&
        (selectedFilters.type?.length ||
          selectedFilters.properties?.length ||
          selectedFilters.destination ||
          (selectedFilters.startDate && selectedFilters.endDate))
    )
);

export const offersListSelector = createSelector(
  [offersSelector, isFavoriteTabSelector],
  (offers, isFavoriteTab) => {
    const helper = Object.values(offers ?? {});

    if (isFavoriteTab) {
      return helper.filter(offer => offer.pref === 'F');
    }

    return helper;
  }
);

export const offersSortedByExpireSelector = createSelector(
  offersListSelector,
  offers =>
    offers
      .slice()
      .sort((a, b) =>
        DateTime.fromISO(a.end).diff(DateTime.fromISO(b.end)).as('milliseconds')
      )
);

export const offersFilteredSelector = createSelector(
  [
    offersSortedByExpireSelector,
    selectedFilterSelector,
    shouldApplyFiltersSelector,
  ],
  (
    offersList,
    {startDate, endDate, destination, properties, type},
    shouldApplyFilters
  ) => {
    if (shouldApplyFilters) {
      const resultsByProperty: Offer[] = [];
      const results = offersList.filter(offer => {
        if (
          (destination || properties?.length) &&
          offer.propertyList.length &&
          !offer.propertyList.includes('---')
        ) {
          const thisProperties = properties?.length
            ? properties
            : destination?.properties;
          const propertyList = thisProperties?.map(property =>
            property.id.toUpperCase()
          );
          if (
            !offer.propertyList.some(
              propertyId => propertyList?.includes(propertyId)
            )
          ) {
            return false;
          }
        }
        resultsByProperty.push(offer);
        if (type?.length) {
          if (
            offer.source === OfferSource.pdb &&
            !type.includes(offer.refinedType)
          ) {
            return false;
          }
          if (offer.source === OfferSource.ocrm) {
            const offerTypes = offer.ocrm.rewards.map(
              reward => reward.refinedType
            );
            if (!type.some(t => offerTypes.includes(t))) {
              return false;
            }
          }
        }
        if (startDate && endDate) {
          const selectedDateRange = Interval.fromDateTimes(
            DateTime.fromISO(startDate).startOf('day'),
            DateTime.fromISO(endDate).plus({days: 1}).startOf('day') // luxon interval is not inclusive of the end date
          );
          const offerDateRange = Interval.fromDateTimes(
            DateTime.fromISO(offer.start).startOf('day'),
            DateTime.fromISO(offer.end).plus({days: 1}).startOf('day')
          );
          if (!selectedDateRange.intersection(offerDateRange)) {
            return false;
          }
        }
        return true;
      });
      return {results, resultsByProperty};
    }
    return {
      results: offersList,
      resultsByProperty: offersList,
    };
  }
);

/**
 * sorting resultsByProperty by start date in order to find the earliest start date
 */

export const resultsByPropertySortedByStartSelector = createSelector(
  offersFilteredSelector,
  ({resultsByProperty}) =>
    resultsByProperty
      .slice()
      .sort((a, b) =>
        DateTime.fromISO(a.start)
          .diff(DateTime.fromISO(b.start))
          .as('milliseconds')
      )
);

export const offersLastQueryTimeSelector = createSelector(
  offersState,
  state => state.lastQueryTimes
);

export const offersFailedSelector = createSelector(
  offersState,
  state => state.failed
);

export const offersLoadingSelector = createSelector(
  offersState,
  state => state?.loading
);

export const offersSaveOfferPrefLoadingSelector = createSelector(
  offersState,
  state => state.saveOfferPrefLoading
);

export const offersOCRMOfferAcceptSelector = createSelector(
  offersState,
  state => state.OCRMOfferAccept
);

export const ocrmOfferAcceptStatusSelector = createSelector(
  offersOCRMOfferAcceptSelector,
  OCRMOfferAccept => OCRMOfferAccept.status
);

export const offersNextSevenDaysSelector = createSelector(
  offersFilteredSelector,
  ({results: offers}) =>
    offers.filter(offer => {
      const date = DateTime.now().plus({days: 7}).endOf('day');
      const offerEndDate = DateTime.fromISO(offer.end);
      return offerEndDate <= date;
    })
);

export const paginatedNextSevenDaysSelector = createSelector(
  offersNextSevenDaysSelector,
  paginateOffers
);

export const offersCurrentMonthSelector = createSelector(
  offersFilteredSelector,
  ({results: offers}) =>
    offers.filter(offer => doesOfferOverlapWithMonth(offer.start, offer.end, 0))
);

export const paginatedCurrentMonthOffersSelector = createSelector(
  offersCurrentMonthSelector,
  paginateOffers
);

export const offersNextMonthSelector = createSelector(
  offersFilteredSelector,
  ({results: offers}) =>
    offers.filter(offer => doesOfferOverlapWithMonth(offer.start, offer.end, 1))
);

export const paginatedNextMonthOffersSelector = createSelector(
  offersNextMonthSelector,
  paginateOffers
);

export const offersThirdMonthFromCurrentSelector = createSelector(
  offersFilteredSelector,
  ({results: offers}) =>
    offers.filter(offer => doesOfferOverlapWithMonth(offer.start, offer.end, 2))
);

export const paginatedThirdMonthOffersSelector = createSelector(
  offersThirdMonthFromCurrentSelector,
  paginateOffers
);

export const selectedOfferIdSelector = createSelector(
  offersState,
  state => state.selectedOfferId
);

export const selectedOfferSelector = createSelector(
  offersState,
  offersSelector,
  (state, offers) =>
    state?.selectedOfferId
      ? (offers[state.selectedOfferId] as Offer)
      : undefined
);

export const selectedStartDateSelector = createSelector(
  selectedFilterSelector,
  selectedFilters => selectedFilters.startDate
);

export const selectedEndDateSelector = createSelector(
  selectedFilterSelector,
  selectedFilters => selectedFilters.endDate
);

export const selectedReservationConfirmationCodeSelector = createSelector(
  selectedFilterSelector,
  state => state.reservationConfirmationCode
);

export const selectedProptertiesSelector = createSelector(
  selectedFilterSelector,
  selectedFilters => selectedFilters.properties
);

export const selectedOfferTypeFilterSelector = createSelector(
  selectedFilterSelector,
  state => state.type
);

export const availablePropertiesSelector = createSelector(
  offersSortedByExpireSelector,
  offers => {
    return offers.reduce<string[]>((acc, offer) => {
      if (
        offer.propertyList.length &&
        !offer.propertyList.every(p => p === '---')
      ) {
        const properties = offer.propertyList.filter(
          property => !acc.includes(property)
        );
        acc = [...acc, ...properties];
      }
      return acc;
    }, []);
  }
);

export const availableFiltersSelector = createSelector(
  [
    offersFilteredSelector,
    availablePropertiesSelector,
    resultsByPropertySortedByStartSelector,
  ],
  ({resultsByProperty}, properties, offersSortedByStart) => {
    const availableTypesAndDates = resultsByProperty.reduce<{
      types: OfferType[];
      startDate: string;
      endDate: string;
    }>(
      (acc, offer, index) => {
        if (index === resultsByProperty.length - 1) {
          acc.endDate = offer.end;
        }
        if (
          offer.source === OfferSource.pdb &&
          !acc.types.includes(offer.refinedType)
        ) {
          acc.types.push(offer.refinedType);
        }
        if (offer.source === OfferSource.ocrm) {
          offer.ocrm.rewards.map(reward => {
            if (!acc.types.includes(reward.refinedType)) {
              acc.types.push(reward.refinedType);
            }
          });
        }
        return acc;
      },
      {startDate: offersSortedByStart[0]?.start || '', endDate: '', types: []}
    );
    return {...availableTypesAndDates, properties};
  }
);

export const availableTypesSelector = createSelector(
  availableFiltersSelector,
  availableFilters => availableFilters.types
);

export const paginationFilteredOffers = createSelector(
  offersFilteredSelector,
  ({results}) => paginateOffers(results)
);

export const propertiesOfferCount = createSelector(
  [offersSortedByExpireSelector, availablePropertiesSelector],
  (offers, properties) => {
    const propertiesCount: {[key: string]: number} = properties.reduce(
      (count, property) => {
        return {...count, [property.toLowerCase()]: 0};
      },
      {}
    );
    offers.map(offer => {
      const propertyList = offer.propertyList.reduce<string[]>(
        (list, property) => {
          if (list.includes(property)) {
            return list;
          }

          return [...list, property];
        },
        []
      );

      return propertyList.map(p => {
        if (p === '---' || p === 'POA') {
          return;
        }

        return (propertiesCount[p.toLowerCase()] += 1);
      });
    });

    return propertiesCount;
  }
);

export const destinationsOfferCount = createSelector(
  [offersSortedByExpireSelector, sortedMarketsSelector, propertiesDataSelector],
  (offers, markets, properties) => {
    const destinationsCount: {[key: string]: number} = markets.reduce(
      (count, market) => {
        return {...count, [market.id]: 0};
      },
      {}
    );

    const destinationMapping = createDestinations(markets, properties);

    offers.forEach(offer => {
      destinationMapping.forEach(destination => {
        const offerExistsInDestination = destination.properties.some(property =>
          offer.propertyList.includes(property.id.toUpperCase())
        );

        if (offerExistsInDestination) {
          destinationsCount[destination.id] += 1;
        }
      });
    });

    return destinationsCount;
  }
);

export const nonMobileResultsByProperty = createSelector(
  [offersFilteredSelector, selectedFilterSelector],
  ({resultsByProperty}, {properties, destination}) => {
    return resultsByProperty.filter(r => {
      if (r.propertyList.includes('---')) {
        if (r.propertyList.every(p => p === '---')) {
          return false;
        }

        if (
          properties?.length &&
          !r.propertyList
            .filter(p => p !== '---')
            .some(p => properties?.map(pr => pr.id.toUpperCase()).includes(p))
        ) {
          return false;
        }

        if (
          !properties?.length &&
          !r.propertyList
            .filter(p => p !== '---')
            .some(
              p =>
                destination?.properties
                  ?.map(pr => pr.id.toUpperCase())
                  .includes(p)
            )
        ) {
          return false;
        }
      }

      return true;
    }).length;
  }
);

export const hasNewOffersSelector = createSelector(
  offersState,
  state => state.hasNewOffers
);

export const isDatesSelectedSelector = createSelector(
  selectedFilterSelector,
  ({startDate, endDate}) => Boolean(startDate && endDate)
);

export const isDomMarketBannerVisibleSelector = createSelector(
  offersState,
  state => state.isDomMarketBannerVisible
);

export const selectedDestinationSelector = createSelector(
  selectedFilterSelector,
  ({destination}) => destination
);

export const focusedCardIdSelector = createSelector(
  offersState,
  state => state.focusedCardId
);

export const selectedGroupingSelector = createSelector(
  offersState,
  state => state.selectedGrouping
);

export const offerIdsInSelectedGroupingSelector = createSelector(
  [
    offersFilteredSelector,
    selectedGroupingSelector,
    offersNextSevenDaysSelector,
    offersCurrentMonthSelector,
    offersNextMonthSelector,
    offersThirdMonthFromCurrentSelector,
  ],
  (
    {results},
    selectedGrouping,
    nextSevenDays,
    currentMonth,
    nextMonth,
    thirdMonth
  ) => {
    if (!selectedGrouping) {
      return results.map(offer => offer.id);
    }

    switch (selectedGrouping.key) {
      case OFFERS_GROUPING.CURRENT_WEEK:
        return nextSevenDays.map(offer => offer.id);
      case OFFERS_GROUPING.CURRENT_MONTH:
        return currentMonth.map(offer => offer.id);
      case OFFERS_GROUPING.NEXT_MONTH:
        return nextMonth.map(offer => offer.id);
      case OFFERS_GROUPING.FOLLOWING_NEXT_MONTH:
        return thirdMonth.map(offer => offer.id);
      default:
        results.map(offer => offer.id);
    }
  }
);

export const nextOfferIdSelector = createSelector(
  [offerIdsInSelectedGroupingSelector, selectedOfferIdSelector],
  (offerIds, selectedOfferId) => {
    if (!selectedOfferId || !offerIds) {
      return undefined;
    }
    const index = offerIds.indexOf(selectedOfferId);

    return offerIds[index + 1];
  }
);

export const previousOfferIdSelector = createSelector(
  [offerIdsInSelectedGroupingSelector, selectedOfferIdSelector],
  (offerIds, selectedOfferId) => {
    if (!selectedOfferId || !offerIds) {
      return undefined;
    }
    const index = offerIds.indexOf(selectedOfferId);

    return offerIds[index - 1];
  }
);

export const selectIsListView = createSelector(
  offersState,
  state => state.isListView
);

export const showOffersRefreshErrorSelector = createSelector(
  offersState,
  state => state.offersRefreshFailCount >= 3
);

export const offersAtSelectedDestinationSelector = createSelector(
  [offersSortedByExpireSelector, selectedDestinationSelector],
  (offers, selectedDestination) => {
    const selectedDestinationPropertiesIDList =
      selectedDestination?.properties.map(properties => properties.id);

    return offers.filter(offer =>
      offer.propertyList.some(
        offerProperty =>
          selectedDestinationPropertiesIDList?.includes(
            offerProperty.toLowerCase()
          )
      )
    );
  }
);
